import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { t } from "@/lib/i18n";
import "@/lib/i18n_p1";
import { CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";

export default function BillingSuccess() {
  const { lang } = useLang();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState({ state: "loading", payment_status: null });
  const attemptsRef = useRef(0);

  const sessionId = params.get("session_id");

  const pollPayment = useCallback(async () => {
    try {
      attemptsRef.current += 1;
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setStatus({ state: "paid", tier_id: data.tier_id });
        await refresh();
        return "done";
      }
      if (data.status === "expired") {
        setStatus({ state: "expired" });
        return "done";
      }
      if (attemptsRef.current >= 8) {
        setStatus({ state: "pending" });
        return "done";
      }
      return "again";
    } catch (e) {
      setStatus({ state: "error", message: e.response?.data?.detail || e.message });
      return "done";
    }
  }, [sessionId, refresh]);

  useEffect(() => {
    if (!sessionId) {
      setTimeout(() => setStatus({ state: "error", message: "Missing session id" }), 0);
      return undefined;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const r = await pollPayment();
      if (r === "again" && !cancelled) setTimeout(tick, 2000);
    };
    const startId = setTimeout(tick, 0);
    return () => { cancelled = true; clearTimeout(startId); };
  }, [sessionId, pollPayment]);

  return (
    <AppShell>
      <div className="max-w-xl mx-auto bg-white border border-[#E9ECEF] p-10 text-center" data-testid="billing-success-card">
        {status.state === "loading" && (
          <>
            <div className="inline-block h-8 w-8 border-2 border-[#0A1128] border-t-transparent animate-spin rounded-full" />
            <p className="mt-6 text-[#495057]">{t(lang,"billing.verifying")}</p>
          </>
        )}
        {status.state === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-[#C65D47] mx-auto" />
            <h1 className="mt-6 font-display text-3xl" data-testid="billing-success-title">{t(lang,"billing.success_title")}</h1>
            <p className="mt-2 text-[#495057]">{t(lang,"billing.success_sub")}</p>
            <button onClick={() => navigate("/dashboard")} className="mt-8 inline-flex items-center gap-2 bg-[#0A1128] text-white px-6 py-3 hover:bg-[#1C2538]" data-testid="billing-go-dashboard">
              {t(lang,"billing.go_dashboard")} <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}
        {(status.state === "pending" || status.state === "expired" || status.state === "error") && (
          <>
            <AlertTriangle className="w-12 h-12 text-[#C65D47] mx-auto" />
            <h1 className="mt-6 font-display text-2xl">{status.state === "expired" ? "Session expired" : status.state === "error" ? "Verification error" : "Still processing"}</h1>
            <p className="mt-2 text-[#495057]">{status.message || "Please refresh or try again."}</p>
            <Link to="/pricing" className="mt-8 inline-flex items-center gap-2 border border-[#0A1128] px-6 py-3 hover:bg-[#F8F9FA]">
              Back to pricing
            </Link>
          </>
        )}
      </div>
    </AppShell>
  );
}
